import { DOCUMENT } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { I18nService } from '../../../core';
import {
  PRIMARY_HEX_PALETTE,
  PRIMARY_HEX_PALETTE_GRID_COLS,
  hexColorsEqual,
  isLightHexColor,
  normalizeHexOrFallback,
  tryParseHexInput,
} from '../../utils/hex-color.util';

/** `after-host`: o pai já posiciona o host (ex.: à direita do círculo da etapa); o popover alinha em `left: 0`. */
export type HexColorPickerPlacement = 'above' | 'after' | 'after-host';

@Component({
  selector: 'app-hex-color-picker-popover',
  standalone: true,
  imports: [LucideAngularModule],
  host: { class: 'block' },
  template: `
    <div
      class="hex-pop"
      [class.hex-pop--above]="placement() === 'above'"
      [class.hex-pop--after]="placement() === 'after'"
      [class.hex-pop--after-host]="placement() === 'after-host'"
      (click)="$event.stopPropagation()"
    >
      <p class="hex-pop-title">{{ t('customFields.editor.colorPickerTitle') }}</p>
      <div class="hex-pop-grid">
        @if (showClear()) {
          <button
            type="button"
            class="hex-dot hex-dot--clear"
            [class.hex-dot--active]="isFallbackSelected()"
            (click)="onClear($event)"
            [attr.aria-label]="t('customFields.editor.colorPickerClearAria')"
          >
            <lucide-icon name="x" class="hex-clear-icon"></lucide-icon>
          </button>
        }
        @for (color of palette(); track color) {
          <button
            type="button"
            class="hex-dot"
            [class.hex-dot--active]="isPresetActive(color)"
            [class.hex-dot--light]="isLightHexColor(color)"
            [style.background]="color"
            (click)="onPickPreset(color, $event)"
          >
            <span class="sr-only">{{ color }}</span>
          </button>
        }
      </div>
      <div class="hex-pop-divider"></div>
      <div class="hex-pop-custom">
        <div
          class="hex-preview"
          [class.hex-preview--light]="isLightHexColor(previewHex())"
          [style.background]="previewHex()"
          aria-hidden="true"
        ></div>
        <input
          type="text"
          class="hex-input"
          [value]="draft()"
          (input)="onHexInput($event)"
          (blur)="commitDraft()"
          (keydown.enter)="onEnter($event)"
          [attr.placeholder]="t('customFields.editor.colorPickerHexPlaceholder')"
          spellcheck="false"
          autocomplete="off"
        />
      </div>
    </div>
  `,
  styles: [
    `
      .hex-pop {
        z-index: 40;
        display: flex;
        flex-direction: column;
        gap: 8px;
        width: fit-content;
        max-width: min(280px, calc(100vw - 24px));
        padding: 8px 10px 8px;
        border: 1px solid var(--border);
        border-radius: 12px;
        background-color: var(--bg-surface);
        background-image: none;
        box-shadow: var(--shadow-lg, 0 10px 24px rgb(15 23 42 / 12%));
        isolation: isolate;
        backdrop-filter: none;
        -webkit-backdrop-filter: none;
      }

      .hex-pop--above {
        position: absolute;
        bottom: calc(100% + 8px);
        left: 0;
      }

      .hex-pop--after {
        position: absolute;
        top: 50%;
        left: calc(100% + 8px);
        transform: translateY(-50%);
      }

      .hex-pop--after-host {
        position: absolute;
        top: 50%;
        left: 0;
        transform: translateY(-50%);
      }

      .hex-pop-title {
        margin: 0;
        font-size: 12px;
        font-weight: 700;
        color: var(--text);
        letter-spacing: 0.02em;
      }

      .hex-pop-grid {
        display: grid;
        grid-template-columns: repeat(${PRIMARY_HEX_PALETTE_GRID_COLS}, 16px);
        gap: 6px;
      }

      .hex-dot {
        box-sizing: border-box;
        width: 16px;
        height: 16px;
        border-radius: 999px;
        border: 1px solid transparent;
        flex-shrink: 0;
        padding: 0;
        margin: 0;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        line-height: 0;
      }

      .hex-dot--light {
        border-color: #cbd5e1;
      }

      .hex-dot--active {
        box-shadow: 0 0 0 2px color-mix(in srgb, var(--focus, #3b82f6) 55%, transparent);
      }

      .hex-dot--clear {
        background: var(--bg-surface);
        border-color: color-mix(in srgb, var(--border) 80%, var(--text));
        color: color-mix(in srgb, var(--muted) 92%, var(--text));
      }

      .hex-clear-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 11px;
        height: 11px;
        flex-shrink: 0;
        pointer-events: none;
      }

      .hex-clear-icon ::ng-deep svg {
        display: block;
        width: 11px;
        height: 11px;
      }

      .hex-pop-divider {
        height: 1px;
        background: color-mix(in srgb, var(--border) 75%, transparent);
      }

      .hex-pop-custom {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .hex-preview {
        width: 28px;
        height: 28px;
        border-radius: 8px;
        border: 1px solid transparent;
        flex-shrink: 0;
      }

      .hex-preview--light {
        border-color: #cbd5e1;
      }

      .hex-input {
        flex: 1;
        min-width: 0;
        height: 28px;
        padding: 0 8px;
        border-radius: 8px;
        border: 1px solid var(--border);
        background: var(--bg-surface);
        color: var(--text);
        font-size: 12px;
        font-variant-numeric: tabular-nums;
        letter-spacing: 0.04em;
      }

      .hex-input::placeholder {
        color: var(--muted);
        opacity: 0.75;
      }

      .hex-input:focus {
        outline: none;
        border-color: color-mix(in srgb, var(--focus, #3b82f6) 45%, var(--border));
        box-shadow: 0 0 0 2px color-mix(in srgb, var(--focus, #3b82f6) 22%, transparent);
      }

      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }
    `,
  ],
})
export class HexColorPickerPopoverComponent implements OnDestroy {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly doc = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly i18n = inject(I18nService);
  readonly t = this.i18n.t.bind(this.i18n);
  readonly isLightHexColor = isLightHexColor;

  /** Valor atual (ex.: cor da opção / etapa). */
  readonly value = input<string>('');
  /** Cor ao usar “limpar” ou hex inválido. */
  readonly fallbackHex = input<string>('#E2E8F0');
  readonly palette = input<readonly string[]>(PRIMARY_HEX_PALETTE);
  readonly showClear = input<boolean>(true);
  readonly placement = input<HexColorPickerPlacement>('above');

  readonly valueCommit = output<string>();
  readonly requestClose = output<void>();

  readonly draft = signal<string>('');

  readonly previewHex = computed(() => {
    const parsed = tryParseHexInput(this.draft());
    if (parsed) return parsed;
    return normalizeHexOrFallback(this.value(), this.fallbackHex());
  });

  constructor() {
    effect(() => {
      const v = this.value();
      const f = this.fallbackHex();
      this.draft.set(normalizeHexOrFallback(v, f));
    });

    /**
     * Captura: o `AppModal` faz stopPropagation no painel; bubble até `document` não ocorre.
     * `setTimeout(0)`: deixa o `click` do botão que abre/fecha correr antes — evita fechar no
     * pointerdown e reabrir no click no mesmo círculo da etapa.
     */
    const onPointerDown = (ev: Event): void => {
      const t = ev.target;
      if (!(t instanceof Node)) return;
      if (!this.host.nativeElement.isConnected) return;
      if (this.host.nativeElement.contains(t)) return;
      const hostEl = this.host.nativeElement;
      setTimeout(() => {
        if (!hostEl.isConnected) return;
        this.requestClose.emit();
      }, 0);
    };
    this.doc.addEventListener('pointerdown', onPointerDown, true);
    this.destroyRef.onDestroy(() =>
      this.doc.removeEventListener('pointerdown', onPointerDown, true)
    );
  }

  ngOnDestroy(): void {
    this.flushIfChanged();
  }

  isPresetActive(preset: string): boolean {
    return hexColorsEqual(this.value(), preset, this.fallbackHex());
  }

  isFallbackSelected(): boolean {
    return normalizeHexOrFallback(this.value(), this.fallbackHex()) === this.fallbackHex();
  }

  onPickPreset(color: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const normalized = normalizeHexOrFallback(color, this.fallbackHex());
    this.draft.set(normalized);
    this.valueCommit.emit(normalized);
  }

  onClear(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const f = this.fallbackHex();
    this.draft.set(normalizeHexOrFallback(f, f));
    this.valueCommit.emit(normalizeHexOrFallback(f, f));
  }

  onHexInput(event: Event): void {
    const el = event.target as HTMLInputElement;
    this.draft.set(el.value);
  }

  commitDraft(): void {
    const normalized = normalizeHexOrFallback(this.draft(), this.fallbackHex());
    this.draft.set(normalized);
    this.valueCommit.emit(normalized);
  }

  onEnter(event: Event): void {
    (event as KeyboardEvent).preventDefault();
    this.commitDraft();
  }

  private flushIfChanged(): void {
    const next = normalizeHexOrFallback(this.draft(), this.fallbackHex());
    const cur = normalizeHexOrFallback(this.value(), this.fallbackHex());
    if (next !== cur) {
      this.valueCommit.emit(next);
    }
  }
}
